import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Users, 
  Phone, 
  CalendarClock, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Globe,
  Hash,
  UserPlus,
  PhoneOutgoing,
  PhoneCall,
  BarChart3,
  ListChecks,
  ExternalLink,
  Shield,
  Activity,
  FileText,
  Clock,
  Flame,
  AlertCircle
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import LiveTeamActivity from "../components/LiveTeamActivity";
import AgentPipelinePanel from "../components/AgentPipelinePanel";
import PerformancePanel from "../components/PerformancePanel";

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [teamSummary, setTeamSummary] = useState(null);
  const [followupSummary, setFollowupSummary] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First fetch the current user to check role
        const meRes = await axios.get(`${API}/auth/me`);
        const adminFlag = ['admin', 'super_admin'].includes(meRes.data?.role);
        setIsAdmin(adminFlag);

        const promises = [
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/leads?limit=5`),
          axios.get(`${API}/calls?limit=5`),
          axios.get(`${API}/reports/productivity?date_range=this_month`),
          axios.get(`${API}/dashboard/followup-summary`),
        ];
        if (adminFlag) {
          promises.push(axios.get(`${API}/dashboard/admin-team-summary`));
        }
        const results = await Promise.all(promises);
        setStats(results[0].data);
        setRecentLeads((results[1].data.leads || []).slice(0, 5));
        setRecentCalls((results[2].data.calls || results[2].data || []).slice(0, 5));
        setAgentPerformance(results[3].data.agent_performance || []);
        setFollowupSummary(results[4].data);
        if (adminFlag && results[5]) {
          setTeamSummary(results[5].data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const channelColors = {
    website: "#1e3a8a",
    meta: "#8b5cf6",
    referral: "#10b981",
    outbound_calling: "#f97316"
  };

  const channelIcons = {
    website: Globe,
    meta: Hash,
    referral: UserPlus,
    outbound_calling: PhoneOutgoing
  };

  const formatChannelData = () => {
    if (!stats?.leads_by_channel) return [];
    return Object.entries(stats.leads_by_channel).map(([name, value]) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: channelColors[name] || "#64748b"
    }));
  };

  const formatProductData = () => {
    if (!stats?.leads_by_product) return [];
    return Object.entries(stats.leads_by_product).map(([name, value]) => ({
      name,
      leads: value
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-50 text-blue-700",
      contacted: "bg-yellow-50 text-yellow-700",
      qualified: "bg-purple-50 text-purple-700",
      converted_to_lfb: "bg-emerald-50 text-emerald-700",
      lost: "bg-red-50 text-red-700"
    };
    return colors[status] || "bg-slate-50 text-slate-700";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Overview of your CRM performance</p>
        </div>
        <Link
          to="/leads"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition-colors btn-press"
          data-testid="add-lead-btn"
        >
          <Users className="w-4 h-4" />
          View Leads
        </Link>
      </div>

      {/* Metric Cards */}
      <div className="bento-grid">
        <Card
          className="metric-card border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 cursor-pointer hover:shadow-md transition-shadow"
          data-testid="within-week-card"
          onClick={() => navigate('/leads?order_timeline=Within%20a%20week')}
        >
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value text-orange-700">{stats?.within_week_total || 0}</p>
                <p className="metric-label text-orange-600 font-semibold">Within a Week</p>
                <p className="text-[11px] text-orange-500 mt-0.5">Leads with imminent booking timeline</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-md">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm text-rose-600">
              <AlertCircle className="w-4 h-4" />
              <span><strong>{stats?.within_week_pending_action || 0}</strong> pending action</span>
              <span className="ml-auto text-xs text-orange-500">View →</span>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="total-leads-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">{stats?.total_leads || 0}</p>
                <p className="metric-label">Total Leads</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-md">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm text-emerald-600">
              <ArrowUpRight className="w-4 h-4" />
              <span>{stats?.new_leads || 0} new</span>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="total-calls-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">{stats?.total_calls || 0}</p>
                <p className="metric-label">Total Calls</p>
              </div>
              <div className="p-2 bg-orange-50 rounded-md">
                <Phone className="w-5 h-5 text-orange-500" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm text-emerald-600">
              <ArrowUpRight className="w-4 h-4" />
              <span>{stats?.completed_calls || 0} completed</span>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="pickpool-lfb-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">
                  {stats?.pick_pool_pushed || 0}
                  <span className="text-slate-300 mx-1.5 font-light">/</span>
                  <span className="text-blue-600">{stats?.lfb_created || 0}</span>
                </p>
                <p className="metric-label">Pick Pool / LFB Created</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-md">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>
                {stats?.total_leads > 0
                  ? `${Math.round((stats?.pick_pool_pushed / stats?.total_leads) * 100)}% of leads`
                  : "0% of leads"}
              </span>
              <span>
                {stats?.pick_pool_pushed > 0
                  ? `${Math.round((stats?.lfb_created / stats?.pick_pool_pushed) * 100)}% LFB`
                  : "0% LFB"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="talk-time-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">{stats?.total_talk_time ? `${Math.floor(stats.total_talk_time / 60)}m` : '0m'}</p>
                <p className="metric-label">Talk Time</p>
              </div>
              <div className="p-2 bg-cyan-50 rounded-md">
                <Clock className="w-5 h-5 text-cyan-500" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm text-slate-500">
              {stats?.total_calls > 0 
                ? `Avg ${Math.round((stats?.total_talk_time || 0) / stats.total_calls / 60)}m per call`
                : "No calls yet"}
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="connected-calls-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">{stats?.completed_calls || 0}</p>
                <p className="metric-label">Connected Calls</p>
              </div>
              <div className="p-2 bg-green-50 rounded-md">
                <PhoneCall className="w-5 h-5 text-green-500" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm text-slate-500">
              {stats?.total_calls > 0 
                ? `${Math.round((stats?.completed_calls / stats?.total_calls) * 100)}% connect rate`
                : "No calls yet"}
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="pending-followups-card">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="metric-value">{stats?.pending_followups || 0}</p>
                <p className="metric-label">Pending Follow-ups</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-md">
                <CalendarClock className="w-5 h-5 text-purple-500" />
              </div>
            </div>
            <Link 
              to="/followups" 
              className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              View all
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row — REMOVED Leads by Category + Leads by Channel as per request */}

      {/* Performance vs Targets — admin sees team, agent sees own */}
      <PerformancePanel />

      {/* Admin Live Team Activity */}
      {isAdmin && <LiveTeamActivity />}

      {/* Admin Agent-wise Lead Pipeline */}
      {isAdmin && <AgentPipelinePanel />}


      {/* Follow-ups Summary — visible to all (scoped server-side) */}
      {followupSummary && followupSummary.agents && followupSummary.agents.length > 0 && (
        <Card className="border-slate-200" data-testid="followup-summary-card">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              Follow-ups Summary
              <span className="ml-3 text-xs font-normal text-slate-500">
                {followupSummary.totals?.pending || 0} pending · {followupSummary.totals?.upcoming || 0} upcoming (next {followupSummary.upcoming_days_window || 7} days)
              </span>
            </CardTitle>
            <Link to="/follow-ups" className="text-sm text-blue-600 hover:underline">View all</Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Account Manager</th>
                    <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4" title="Open follow-ups whose scheduled time is already past">Pending</th>
                    <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4" title={`Follow-ups due in the next ${followupSummary.upcoming_days_window || 7} days`}>Upcoming</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {followupSummary.agents.map((am) => (
                    <tr key={am.user_id} className="hover:bg-slate-50" data-testid={`followup-row-${am.user_id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-900 font-medium text-sm">{am.name?.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{am.name}</p>
                            <p className="text-xs text-slate-500">{am.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`followup-pending-${am.user_id}`}>
                        <span className={`font-semibold ${am.pending_count > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          {am.pending_count}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`followup-upcoming-${am.user_id}`}>
                        <span className={`font-semibold ${am.upcoming_count > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                          {am.upcoming_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Performance Section - Admin only */}
      {isAdmin && (
      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Agent Performance
          </CardTitle>
          <Link to="/reports" className="text-sm text-blue-600 hover:underline">View full report</Link>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Agent</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Leads</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Talk Time</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Pick Pool Push</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">LFB Created</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">No. of Calls</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4" title="Calls > 5 min with no notes">Missing Notes</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4" title="Leads whose status changed in the selected range">Stage Updates</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4" title="Leads created within the selected range and assigned to this agent">New Leads</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Follow Up Missed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {agentPerformance.length > 0 ? (
                  agentPerformance.map((agent) => (
                    <tr key={agent.user_id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-900 font-medium text-sm">
                              {agent.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{agent.name}</p>
                            <p className="text-xs text-slate-500">{agent.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-slate-900">{agent.leads_assigned}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-emerald-600">
                          {agent.total_talk_time ? `${Math.floor(agent.total_talk_time / 60)}m` : '0m'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-green-600">{agent.pick_pool_pushed || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-blue-600">{agent.lfb_created || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-slate-900">{agent.calls_made}</span>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`agent-missing-notes-${agent.user_id}`}>
                        <span className={`font-semibold ${agent.calls_missing_notes > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          {agent.calls_missing_notes || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`agent-stage-updates-${agent.user_id}`}>
                        <span className={`font-semibold ${agent.stage_updates === 0 && agent.calls_made >= 40 ? 'text-red-600' : 'text-slate-900'}`}>
                          {agent.stage_updates || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`agent-new-leads-${agent.user_id}`}>
                        <span className={`font-semibold ${(agent.new_leads_created || 0) < 2 ? 'text-red-600' : 'text-slate-900'}`}>
                          {agent.new_leads_created || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-semibold ${agent.followups_missed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          {agent.followups_missed || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-slate-400">
                      No agent data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Recent Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900">Recent Leads</CardTitle>
            <Link to="/leads" className="text-sm text-blue-600 hover:underline">View all</Link>
          </CardHeader>
          <CardContent>
            {recentLeads.length > 0 ? (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/leads/${lead.id}`}
                    className="flex items-center gap-4 p-3 rounded-md hover:bg-slate-50 transition-colors"
                    data-testid={`recent-lead-${lead.id}`}
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">
                        {lead.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                      <p className="text-sm text-slate-500 truncate">{lead.company_name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-md ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                No leads yet. Create your first lead!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900">Recent Calls</CardTitle>
            <Link to="/calls" className="text-sm text-blue-600 hover:underline">View all</Link>
          </CardHeader>
          <CardContent>
            {recentCalls.length > 0 ? (
              <div className="space-y-3">
                {recentCalls.map((call) => (
                  <div
                    key={call.id}
                    className="flex items-center gap-4 p-3 rounded-md hover:bg-slate-50 transition-colors"
                    data-testid={`recent-call-${call.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      call.status === 'completed' ? 'bg-emerald-100' : 
                      call.status === 'failed' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      <Phone className={`w-5 h-5 ${
                        call.status === 'completed' ? 'text-emerald-600' : 
                        call.status === 'failed' ? 'text-red-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm text-slate-900">{call.phone_number}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(call.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span className={`text-xs font-medium capitalize ${
                      call.status === 'completed' ? 'text-emerald-600' :
                      call.status === 'failed' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {call.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                No calls recorded yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
